// Composable para lógica de Expense Sheets
// Extrae funcionalidad específica de expense sheets de useBaseModule

import { useI18n } from "vue-i18n";

/**
 * Composable para manejar estados, colores y operaciones de expense sheets
 */
export function useExpenseSheets() {
  const { t } = useI18n();

  /**
   * Obtiene el color correspondiente a un estado de expense sheet
   * @param status - Código de estado (-1 a 4)
   * @returns Color en formato string
   */
  function getStatusColor(status: number): string {
    let color = "green";

    switch (status) {
      case -1: // PREPARING
      case 0: // PENDING
        color = "orange";
        break;
      case 2: // DECLINED
      case 3: // CANCELED
        color = "red";
        break;
      case 4: // PROCESSED
        color = "#2196F3";
        break;
    }

    return color;
  }

  /**
   * Obtiene el texto traducido para un estado de expense sheet
   * @param status - Código de estado (-1 a 4)
   * @returns Texto del estado en mayúsculas
   */
  function getStatusText(status: number): string {
    let text = "???";

    switch (status) {
      case -1: // PREPARING
        text = t("wkt.client.expensesheets.status-type.preparing");
        break;
      case 0: // PENDING
        text = t("wkt.client.expensesheets.status-type.pending");
        break;
      case 1: // APPROVED
        text = t("wkt.client.expensesheets.status-type.approved");
        break;
      case 2: // DECLINED
        text = t("wkt.client.expensesheets.status-type.denied");
        break;
      case 3: // CANCELED
        text = t("wkt.client.expensesheets.status-type.canceled");
        break;
      case 4: // PROCESSED
        text = t("wkt.client.expensesheets.status-type.processed");
        break;
    }

    return text.toUpperCase();
  }

  /**
   * Obtiene el texto traducido para un tipo de formato
   * @param formatType - Tipo de formato (0 = ticket, 1 = invoice)
   * @returns Texto del tipo de formato
   */
  function getFormatTypeText(formatType: number): string {
    let text = "???";

    switch (formatType) {
      case 0:
        text = t(
          "wkt.client.expensesheets.detail-window.items.format-type.ticket"
        );
        break;
      case 1:
        text = t(
          "wkt.client.expensesheets.detail-window.items.format-type.invoice"
        );
        break;
    }

    return text;
  }

  /**
   * Obtiene el texto traducido para un método de pago
   * @param paymentMethod - Método de pago (0 = cash, 1 = company_card, 2 = other)
   * @returns Texto del método de pago
   */
  function getPaymentMethodText(paymentMethod: number): string {
    let text = "???";

    switch (paymentMethod) {
      case 0:
        text = t(
          "wkt.client.expensesheets.detail-window.items.payment-method-type.cash"
        );
        break;
      case 1:
        text = t(
          "wkt.client.expensesheets.detail-window.items.payment-method-type.company_card"
        );
        break;
      case 2:
        text = t(
          "wkt.client.expensesheets.detail-window.items.payment-method-type.other"
        );
        break;
    }

    return text;
  }

function getStatusSeverity(status: number): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
  switch (status) {
    case -1: // PREPARING
    case 0:  // PENDING
      return 'warn'
    case 1:  // APPROVED
      return 'success'
    case 2:  // DECLINED
    case 3:  // CANCELED
      return 'danger'
    case 4:  // PROCESSED
      return 'info'
    default:
      return 'secondary'
  }
}

  return {
    getStatusColor,
    getStatusText,
    getStatusSeverity,
    getFormatTypeText,
    getPaymentMethodText,
  };
}
